/* c:\App\ERP\erp-system-venture-2020\src\app\smre-fee\smre-fee-report\smre-fee-report.component.ts */
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { Auth } from '@angular/fire/auth';
import { InvoiceService } from '../services/invoice.service';
import { FeeRateService, SmrsRate } from '../services/fee-rate.service';
import { StoreService } from '../services/store.service';
import { ExchangeRateService } from '../services/exchange-rate.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { AccessService } from '../services/access.service';
import { PdfGeneratorService } from '../services/pdf-generator.service';
import { BrandService } from '../services/brand.service';
import { CompanyService } from '../services/company.service';
import { Brand } from '../models/brand.model';

interface StoreData {
  id: string;
  companyId: string;
  companyName: string;
  storeName: string;
  country: string;
  rates: { [rateId: string]: number };
  total: number;
  totalRevenue: number;
  kcegOwned?: boolean;
}

/** Width (px) the SMRS statement is laid out at for the PDF; html2pdf scales the capture to the page width. */
const PDF_BASE_WIDTH_PX = 1200;

@Component({
  selector: 'app-smrs-fee-report',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule],
  providers: [ExchangeRateService],
  templateUrl: './smrs-fee-report.component.html',
  styleUrl: './smrs-fee-report.component.css'
})
export class SmrsFeeReportComponent implements OnInit, OnDestroy {
  private invoiceService = inject(InvoiceService);
  private feeRateService = inject(FeeRateService);
  private storeService = inject(StoreService);
  private exchangeRateService = inject(ExchangeRateService);
  private accessService = inject(AccessService);
  private auth = inject(Auth);
  private pdfService = inject(PdfGeneratorService);
  private brandService = inject(BrandService);
  private companyService = inject(CompanyService);

  isExporting = false;
  selectedBrand: Brand | null = null;
  private brandSubscription?: Subscription;

  rateTypes: SmrsRate[] = [];
  gridData: StoreData[] = [];
  filteredGridData: StoreData[] = [];
  
  allUniqueCompanies: string[] = [];
  allUniqueStores: string[] = [];
  companiesForFilter: string[] = [];
  storesForFilter: string[] = [];
  selectedCompanies: Set<string> = new Set();
  selectedStores: Set<string> = new Set();
  showCompanyFilter = false;
  showStoreFilter = false;
  showCountryFilterDropdown = false;
  filterCountry: string = 'All';
  availableCountries: string[] = [];
  selectedCompaniesCount = 0;
  selectedStoresCount = 0;
  kpiData: { [rateId: string]: number; total: number; totalRevenue: number } = { total: 0, totalRevenue: 0 };
  incentiveStats = {
    rateNames: [] as string[],
    lfc: {} as { [key: string]: number },
    kceg: {} as { [key: string]: number },
    kcegRates: {} as { [key: string]: number },
    lfcRates: {} as { [key: string]: number },
    total: {} as { [key: string]: number },
    grandTotalLfc: 0,
    grandTotalKceg: 0,
    grandTotal: 0
  };
  periods: string[] = [];
  selectedPeriod: string = '';
  isLoading = false;
  companyNameIdMap = new Map<string, string>();
  companyIdNameMap = new Map<string, string>();
  storeNameIdMap = new Map<string, string>();
  storeIdNameMap = new Map<string, string>();

  // Cache
  stores: any[] = [];
  companies: any[] = [];
  brands: Brand[] = [];
  private loadSeq = 0;
  private excludedCompanyIds = new Set<string>();
  loadError = false;
  private exchangeRateCache = new Map<string, { rate: number, date?: Date }>();

  async ngOnInit() {
    this.generatePeriods();
    this.selectedPeriod = this.periods[0]; // Default to current/latest

    this.isLoading = true;
    try {
      // Fetch static data
      this.brands = await firstValueFrom(this.brandService.getBrands());
      const allStores = await firstValueFrom(this.storeService.getStores());
      const comps = await firstValueFrom(this.invoiceService.getCompanies());
      
      const user = this.auth.currentUser;
      let finalComps = comps;
      let finalStores = allStores;

      if (user) {
        const profile = await firstValueFrom(this.accessService.getUserProfile(user.uid));
        const scope = await this.accessService.getUserPageScope('smrs-fee-report', profile);
        if (scope.isRestricted) {
          const allowedCompanySet = new Set(scope.companyIds);
          const allowedStoreSet = new Set(scope.storeIds.map(s => s.toLowerCase()));
          finalComps = comps.filter((c: any) => allowedCompanySet.has(c.id));
          finalStores = allStores.filter((s: any) => {
            const sid = (s.storeId || s.id || '').toString().toLowerCase();
            return allowedStoreSet.has(sid);
          });
        }
      }

      // Companies that exist but are not part of this report (soft-deleted, or outside the user's owner scope) must never
      // contribute: their aggregations would otherwise fall into the "unmapped company" fallback below.
      const visibleCompanyIds = new Set(finalComps.map((c: any) => c.id));
      const everyCompany = await firstValueFrom(this.companyService.getCompanies());
      this.excludedCompanyIds = new Set(everyCompany.map((c: any) => c.id).filter((id: string) => id && !visibleCompanyIds.has(id)));

      this.stores = finalStores;
      this.companies = finalComps.map((c: any) => {
        return { ...c, brand: this.canonicalBrand(c.brand || '') };
      });
      
      this.brandSubscription = this.brandService.selectedBrand$.subscribe(brand => {
        const brandChanged = this.selectedBrand?.id !== brand?.id;
        this.selectedBrand = brand;
        if (brandChanged && this.selectedPeriod && this.companies.length > 0) {
          this.loadData();
        }
      });

      if (this.selectedBrand && this.companies.length > 0 && this.gridData.length === 0) {
        await this.loadData();
      }
    } catch (err) {
      console.error('Error initializing report:', err);
    } finally {
      this.isLoading = false;
    }
  }

  ngOnDestroy() {
    if (this.brandSubscription) {
      this.brandSubscription.unsubscribe();
    }
  }

  getBrandName(id: string): string {
    const brand = this.brands.find(b => b.id === id);
    return brand ? brand.name : id;
  }

  get activeBrandName(): string {
    return this.selectedBrand?.name || '';
  }

  /**
   * Resolves a brand id or name to the canonical name of a known brand (LFC, K-Photo Lavie, Konobox, ...).
   * Anything that does not resolve to a known brand keeps the legacy behaviour and counts as LFC.
   */
  private canonicalBrand(raw: string): string {
    const name = this.getBrandName(raw || '').trim();
    const known = this.brands.find(b => (b.name || '').trim().toLowerCase() === name.toLowerCase());
    return known ? known.name.trim() : 'LFC';
  }

  /** True when a brand id or name matches one of the brands in the `brands` collection. */
  private isKnownBrand(raw: string): boolean {
    const v = (raw || '').trim().toLowerCase();
    return !!v && this.brands.some(b => (b.id || '').toLowerCase() === v || (b.name || '').trim().toLowerCase() === v);
  }

  /**
   * SMRS rates of a brand for a period. Rates stored with an old brand id that no longer exists in `brands` (for example the legacy LFC id)
   * still count as LFC, but only when the period has no rates stored with the real brand id: otherwise both sets are loaded and the same
   * revenue keys are counted twice (duplicate rate columns, inflated revenue and fees).
   */
  private ratesForBrand(allRates: SmrsRate[], period: string, brand: string): SmrsRate[] {
    const rateBrand = (r: SmrsRate): string => (r as any).brand || '';
    const inPeriod = allRates.filter(r => r.period === period);
    const exact = inPeriod.filter(r => this.isKnownBrand(rateBrand(r)) && this.canonicalBrand(rateBrand(r)) === brand);
    if (exact.length > 0) return exact;
    return inPeriod.filter(r => !this.isKnownBrand(rateBrand(r)) && this.canonicalBrand(rateBrand(r)) === brand);
  }

  /** Brand whose stores, rates and allocations are shown. No selected brand keeps the legacy default (K-Photo Lavie). */
  get targetBrand(): string {
    return this.activeBrandName.trim() || 'K-Photo Lavie';
  }

  /** Label of the brand-partner allocation row (the non-KCEG share of the fee). */
  get partnerLabel(): string {
    const brand = this.targetBrand;
    if (brand === 'K-Photo Lavie') return 'Commission';
    if (brand === 'LFC') return 'LK';
    return `${brand} Partner`;
  }

  /** File-name-safe brand name for the PDF. */
  private get brandFileName(): string {
    return this.targetBrand.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'Brand';
  }

  generatePeriods() {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    
    for (let i = 0; i < 24; i++) {
      let y = currentYear;
      let m = currentMonth - i;
      while (m <= 0) {
        m += 12;
        y -= 1;
      }
      this.periods.push(`${y}/${m.toString().padStart(2, '0')}`);
    }
  }

  async onPeriodChange() {
    await this.loadData();
  }

  async loadData() {
    if (!this.selectedPeriod) return;
    // Brand, period and initial loads can overlap; only the newest load may publish rows (no stale or cross-brand data).
    const loadSeq = ++this.loadSeq;
    this.isLoading = true;
    this.loadError = false;
    this.gridData = [];
    this.rateTypes = [];
    this.closeFilters();

    try {
      // 1. Fetch Rates for the period
      const targetBrand = this.targetBrand;
      const allRates = await firstValueFrom(this.feeRateService.getRates());
      if (loadSeq !== this.loadSeq) return;
      this.rateTypes = this.ratesForBrand(allRates, this.selectedPeriod, targetBrand);

      // 2. Fetch Aggregations
      const aggregations = await this.invoiceService.getAggregations(this.selectedPeriod);
      if (loadSeq !== this.loadSeq) return;

      // 3. Process Data
      const storeMap = new Map<string, any>();
      this.stores.forEach(s => {
        const sid = String(s.storeId).trim();
        if (sid) storeMap.set(sid, s);
      });
      
      const companyMap = new Map<string, any>();
      this.companies.forEach(c => companyMap.set(c.id, c));

      const groupedData = new Map<string, any>();

      for (const agg of aggregations) {
        const storeId = String(agg['storeId'] || '').trim();
        const store = storeMap.get(storeId);
        // Soft-deleted stores keep their historical aggregations but never contribute to the report.
        if (store?.isDeleted === true) continue;

        let companyIds: string[] = [];
        if (agg['companyId']) {
          companyIds = [agg['companyId']];
        } else if (store) {
          companyIds = store.companyIds || (store.companyId ? [store.companyId] : []);
        }

        if (companyIds.length === 0 && !agg['isAdjustment']) continue;

        for (const companyId of companyIds) {
          if (this.excludedCompanyIds.has(companyId)) continue;
          const company = companyMap.get(companyId);
          
          if (company) {
            if (company.brand !== targetBrand) continue;   // company.brand is already canonical
          } else {
            // Assume LFC if no company is mapped and we can't explicitly verify the brand
            if (targetBrand !== 'LFC') continue;
          }

          const key = `${companyId}_${storeId}`;
          if (!groupedData.has(key)) {
            let country = store?.country || agg['country'] || 'USA';
            if (country === '미국' || country === '미국2') country = 'USA';
            
            let currency = store?.currency || 'USD';
            if (['Canada', '캐나다'].includes(country)) {
                currency = 'CAD';
            }

            groupedData.set(key, {
              id: storeId,
              companyId: companyId,
              companyName: company?.company || 'Unknown Company',
              storeName: store?.storeName || agg['storeName'] || storeId || 'Unknown Store',
              country: country,
              currency: currency,
              kcegOwned: company?.kcegOwned || false,
              isMoneyMarket: company?.isMoneyMarket || false,
              aggs: []
            });
          }
          groupedData.get(key).aggs.push(agg);
        }
      }

      const rows: StoreData[] = [];

      for (const data of groupedData.values()) {
        let exchangeRate = 1;
        if (data.currency !== 'USD') {
            const rateData = await this.getExchangeRate(data.currency, this.selectedPeriod);
            exchangeRate = rateData.rate;
        }

        const row: StoreData = {
          id: data.id,
          companyId: data.companyId,
          companyName: data.companyName,
          storeName: data.storeName,
          country: data.country,
          rates: {},
          total: 0,
          totalRevenue: 0,
          kcegOwned: data.kcegOwned,
        };

        let totalFee = 0;
        let totalRevenue = 0;
        
        for (const rate of this.rateTypes) {
           let revenue = 0;
           for (const agg of data.aggs) {
             revenue += this.invoiceService.calculateRevenueForRate(agg, rate);
           }
           
           const fee = data.isMoneyMarket ? 0 : revenue * (rate.rate / 100);
           const feeUSD = fee * exchangeRate;
           const revUSD = revenue * exchangeRate;
           
           row.rates[rate.id!] = feeUSD;
           totalFee += feeUSD;
           totalRevenue += revUSD;
        }

        if (totalRevenue !== 0 || totalFee !== 0) {
          row.total = totalFee;
          row.totalRevenue = totalRevenue;
          rows.push(row);
        }
      }
      
      if (loadSeq !== this.loadSeq) return;
      this.gridData = rows.sort((a, b) => {
        const companyCompare = a.companyName.localeCompare(b.companyName);
        if (companyCompare !== 0) return companyCompare;
        return a.id.localeCompare(b.id);
      });

      // Initialize master lists and selections
      const allTabCompanies = new Set(
        this.companies
          .filter(c => c.brand === targetBrand)
          .map(c => c.company)
      );
      this.gridData.forEach(r => allTabCompanies.add(r.companyName));
      this.allUniqueCompanies = Array.from(allTabCompanies).sort();

      this.allUniqueStores = Array.from(new Set(this.gridData.map(r => r.storeName))).sort();
      
      const countries = new Set<string>();
      this.gridData.forEach(r => {
        if (r.country) countries.add(r.country);
      });
      this.availableCountries = Array.from(countries).sort();

      this.companyNameIdMap.clear();
      this.companyIdNameMap.clear();
      this.storeNameIdMap.clear();
      this.storeIdNameMap.clear();

      this.companies.forEach(c => {
        this.companyNameIdMap.set(c.company, c.id);
        this.companyIdNameMap.set(c.id, c.company);
      });

      this.gridData.forEach(r => {
        this.companyNameIdMap.set(r.companyName, r.companyId);
        this.companyIdNameMap.set(r.companyId, r.companyName);
        this.storeNameIdMap.set(r.storeName, r.id);
        this.storeIdNameMap.set(r.id, r.storeName);
      });

      await this.initPreferences();

    } catch (error) {
      console.error('Error loading report data:', error);
      if (loadSeq === this.loadSeq) this.loadError = true;
    } finally {
      if (loadSeq === this.loadSeq) this.isLoading = false;
    }
  }

  async initPreferences() {
    const user = this.auth.currentUser;
    if (!user) {
      this.filterCountry = 'All';
      this.selectedCompanies = new Set(this.allUniqueCompanies);
      this.selectedStores = new Set(this.allUniqueStores);
      this.updateFilters();
      return;
    }

    try {
      const prefs = await this.accessService.getUserSmrsPreferences(user.uid);

      if (prefs) {
        const savedCountry = prefs['country'];
        if (savedCountry && (savedCountry === 'All' || this.availableCountries.includes(savedCountry))) {
          this.filterCountry = savedCountry;
        } else {
          this.filterCountry = 'All';
        }

        const savedIds = prefs['companyIds'];
        if (savedIds && Array.isArray(savedIds)) {
          const validNames = new Set<string>();
          savedIds.forEach(id => {
            const name = this.companyIdNameMap.get(id);
            if (name && this.allUniqueCompanies.includes(name)) {
              validNames.add(name);
            }
          });

          if (validNames.size > 0) {
            this.selectedCompanies = validNames;
          } else {
            this.selectedCompanies = savedIds.length > 0 ? new Set(this.allUniqueCompanies) : new Set();
          }
        } else {
          this.selectedCompanies = new Set(this.allUniqueCompanies);
        }

        const savedStoreIds = prefs['storeIds'];
        if (savedStoreIds && Array.isArray(savedStoreIds)) {
          const validStoreNames = new Set<string>();
          savedStoreIds.forEach(id => {
            const name = this.storeIdNameMap.get(id);
            if (name && this.allUniqueStores.includes(name)) {
              validStoreNames.add(name);
            }
          });

          if (validStoreNames.size > 0) {
            this.selectedStores = validStoreNames;
          } else {
            this.selectedStores = savedStoreIds.length > 0 ? new Set(this.allUniqueStores) : new Set();
          }
        } else {
          this.selectedStores = new Set(this.allUniqueStores);
        }
      } else {
        this.filterCountry = 'All';
        this.selectedCompanies = new Set(this.allUniqueCompanies);
        this.selectedStores = new Set(this.allUniqueStores);
      }
    } catch (e) {
      console.error('Error loading SMRS preferences', e);
      this.filterCountry = 'All';
      this.selectedCompanies = new Set(this.allUniqueCompanies);
      this.selectedStores = new Set(this.allUniqueStores);
    }

    this.updateFilters();
  }

  async savePreferences() {
    const user = this.auth.currentUser;
    if (!user) return;

    const companyIds = Array.from(this.selectedCompanies)
      .map(name => this.companyNameIdMap.get(name))
      .filter(id => id !== undefined) as string[];

    const storeIds = Array.from(this.selectedStores)
      .map(name => this.storeNameIdMap.get(name))
      .filter(id => id !== undefined) as string[];

    try {
      await this.accessService.saveUserSmrsPreferences(user.uid, { country: this.filterCountry, companyIds, storeIds });
    } catch (e) {
      console.error('Error saving SMRS preferences', e);
    }
  }

  async getExchangeRate(currency: string, period: string): Promise<{ rate: number, date?: Date }> {
    const key = `${currency}_${period}`;
    if (this.exchangeRateCache.has(key)) {
      return this.exchangeRateCache.get(key)!;
    }

    const rateData = await this.invoiceService.getExchangeRate(currency, period);
    if (rateData !== null) {
      this.exchangeRateCache.set(key, rateData);
      return rateData;
    }

    try {
      const apiData = await this.exchangeRateService.getCADtoUSDRate(period);
      if (apiData) {
        const data = { rate: apiData.rate, date: apiData.date };
        this.exchangeRateCache.set(key, data);
        return data;
      }
    } catch (error) {
      console.warn('Error fetching exchange rate:', error);
    }
    
    const defaultData = { rate: 1 };
    this.exchangeRateCache.set(key, defaultData);
    return defaultData;
  }

  get kcegOwnedCompanies() {
    return this.companiesForFilter.filter(name => {
      const c = this.companies.find(comp => comp.company === name);
      return c && c.kcegOwned;
    });
  }

  get isKcegOwnedSelected() {
    const kceg = this.kcegOwnedCompanies;
    const activeSelected = this.companiesForFilter.filter(name => this.selectedCompanies.has(name));
    return kceg.length > 0 && activeSelected.length === kceg.length && kceg.every(name => this.selectedCompanies.has(name));
  }

  updateFilters() {
    const countryMatch = (row: StoreData) => this.filterCountry === 'All' || row.country === this.filterCountry;

    // Determine the list of stores available in the dropdown based on selected companies AND Country
    const availableStores = new Set(
      this.gridData
        .filter(row => this.selectedCompanies.has(row.companyName) && countryMatch(row))
        .map(row => row.storeName)
    );
    this.storesForFilter = this.allUniqueStores.filter(s => availableStores.has(s));

    // Show all companies for the current tab, regardless of grid data
    this.companiesForFilter = [...this.allUniqueCompanies];

    // Determine available countries based on selected companies and stores
    const availableCountriesSet = new Set<string>();
    this.gridData
        .filter(row => this.selectedCompanies.has(row.companyName) && this.selectedStores.has(row.storeName))
        .forEach(row => {
            if (row.country) availableCountriesSet.add(row.country);
        });
    this.availableCountries = Array.from(availableCountriesSet).sort();

    this.selectedCompaniesCount = this.companiesForFilter.filter(c => this.selectedCompanies.has(c)).length;
    this.selectedStoresCount = this.storesForFilter.filter(s => this.selectedStores.has(s)).length;

    // Filter the grid for display
    this.filteredGridData = this.gridData.filter(row => {
     return countryMatch(row) && this.selectedCompanies.has(row.companyName) && this.selectedStores.has(row.storeName);
    });
    this.calculateKpis();
    this.calculateIncentiveStats();
  }

  calculateKpis() {
    const visibleRows = this.filteredGridData;
    
    const newKpiData: { [rateId: string]: number; total: number; totalRevenue: number } = { total: 0, totalRevenue: 0 };
    
    this.rateTypes.forEach(rate => {
      if (rate.id) {
        newKpiData[rate.id] = 0;
      }
    });

    for (const row of visibleRows) {
      for (const rate of this.rateTypes) {
        if (rate.id) {
          newKpiData[rate.id] += row.rates[rate.id] || 0;
        }
      }
      newKpiData.total += row.total || 0;
      newKpiData.totalRevenue += row.totalRevenue || 0;
    }

    this.kpiData = newKpiData;
  }

  calculateIncentiveStats() {
    this.incentiveStats = {
      rateNames: [],
      lfc: {},
      kceg: {},
      kcegRates: {},
      lfcRates: {},
      total: {},
      grandTotalLfc: 0,
      grandTotalKceg: 0,
      grandTotal: 0
    };

    const rateNamesSet = new Set<string>();
    const totalsByRateName: { [key: string]: number } = {};
    const kcegTotalsByRateName: { [key: string]: number } = {};
    const kcegRatesByRateName: { [key: string]: number } = {};
    const lfcRatesByRateName: { [key: string]: number } = {};
    const lfcTotalsByRateName: { [key: string]: number } = {};
    const rateOrderMap = new Map<string, number>();

    // Initialize with all rates for the period, so they show up even with 0 data
    this.rateTypes.forEach(rate => {
      const rateName = `${rate.name || 'Unknown'} Fee (${rate.rate}%)`;
      rateNamesSet.add(rateName);
      rateOrderMap.set(rateName, rate.order ?? 9999);

      const kcegRateVal = (rate as any).kcegRate;
      const kcegRate = (kcegRateVal !== undefined && kcegRateVal !== null) ? Number(kcegRateVal) : 0;
      const lfcRate = (rate.rate || 0) - kcegRate;

      kcegRatesByRateName[rateName] = kcegRate;
      lfcRatesByRateName[rateName] = lfcRate;
      
      totalsByRateName[rateName] = 0;
      kcegTotalsByRateName[rateName] = 0;
      lfcTotalsByRateName[rateName] = 0;
    });

    this.filteredGridData.forEach(row => {
      this.rateTypes.forEach(rate => {
        if (rate.id && row.rates[rate.id]) {
          const fee = row.rates[rate.id];
          const rateName = `${rate.name || 'Unknown'} Fee (${rate.rate}%)`;
          
          totalsByRateName[rateName] = (totalsByRateName[rateName] || 0) + fee;

          const kcegRateVal = (rate as any).kcegRate;
          const kcegRate = (kcegRateVal !== undefined && kcegRateVal !== null) ? Number(kcegRateVal) : 0;
          const lfcRate = (rate.rate || 0) - kcegRate;

          let kcegAmount = 0;
          let lfcAmount = 0;
          
          if (rate.rate && rate.rate !== 0) {
            kcegAmount = fee * (kcegRate / rate.rate);
            lfcAmount = fee * (lfcRate / rate.rate);
          }

          kcegTotalsByRateName[rateName] = (kcegTotalsByRateName[rateName] || 0) + kcegAmount;
          lfcTotalsByRateName[rateName] = (lfcTotalsByRateName[rateName] || 0) + lfcAmount;
        }
      });
    });

    this.incentiveStats.rateNames = Array.from(rateNamesSet).sort((a, b) => {
      const orderA = rateOrderMap.get(a) ?? 9999;
      const orderB = rateOrderMap.get(b) ?? 9999;
      if (orderA !== orderB) return orderA - orderB;
      return a.localeCompare(b);
    });

    this.incentiveStats.rateNames.forEach(name => {
      const total = totalsByRateName[name] || 0;
      const kceg = kcegTotalsByRateName[name] || 0;
      const lfc = lfcTotalsByRateName[name] || 0;

      this.incentiveStats.total[name] = total;
      this.incentiveStats.lfc[name] = lfc;
      this.incentiveStats.kceg[name] = kceg;
      this.incentiveStats.kcegRates[name] = kcegRatesByRateName[name] || 0;
      this.incentiveStats.lfcRates[name] = lfcRatesByRateName[name] || 0;

      this.incentiveStats.grandTotal += total;
      this.incentiveStats.grandTotalLfc += lfc;
      this.incentiveStats.grandTotalKceg += kceg;
    });
  }

  onAction(row: StoreData) {
    console.log('Action clicked for', row);
    // Implement action logic here (e.g., view details, edit)
  }

  // Filter UI Helpers
  toggleCompanyFilter() {
    this.showCompanyFilter = !this.showCompanyFilter;
    this.showStoreFilter = false;
    this.showCountryFilterDropdown = false;
  }

  toggleStoreFilter() {
    this.showStoreFilter = !this.showStoreFilter;
    this.showCompanyFilter = false;
    this.showCountryFilterDropdown = false;
  }

  toggleCountryFilterDropdown() {
    this.showCountryFilterDropdown = !this.showCountryFilterDropdown;
    this.showCompanyFilter = false;
    this.showStoreFilter = false;
  }

  selectCountryFilter(country: string) {
    this.filterCountry = country;
    this.showCountryFilterDropdown = false;
    this.savePreferences();
    this.updateFilters();
  }

  closeFilters() {
    this.showCompanyFilter = false;
    this.showStoreFilter = false;
    this.showCountryFilterDropdown = false;
  }

  toggleCompanySelection(company: string) {
    if (this.selectedCompanies.has(company)) {
      this.selectedCompanies.delete(company);
    } else {
      this.selectedCompanies.add(company);
    }
    this.savePreferences();
    this.updateFilters();
  }

  toggleAllCompanies() {
    if (this.selectedCompanies.size === this.allUniqueCompanies.length) this.selectedCompanies.clear();
    else this.selectedCompanies = new Set(this.allUniqueCompanies);
    this.savePreferences();
    this.updateFilters();
  }

  toggleKcegOwnedFilter() {
    const kceg = this.kcegOwnedCompanies;
    if (this.isKcegOwnedSelected) {
      kceg.forEach(name => this.selectedCompanies.delete(name));
    } else {
      this.companiesForFilter.forEach(name => this.selectedCompanies.delete(name));
      kceg.forEach(name => this.selectedCompanies.add(name));
    }
    this.savePreferences();
    this.updateFilters();
  }

  toggleStoreSelection(store: string) {
    if (this.selectedStores.has(store)) this.selectedStores.delete(store);
    else this.selectedStores.add(store);
    this.savePreferences();
    this.updateFilters();
  }

  toggleAllStores() {
    if (this.selectedStores.size === this.allUniqueStores.length) this.selectedStores.clear();
    else this.selectedStores = new Set(this.allUniqueStores);
    this.savePreferences();
    this.updateFilters();
  }

  async openPdf() {
    const element = document.getElementById('pdf-report-content');
    if (!element) return;

    this.isExporting = true;

    // Open window immediately to avoid popup blockers
    let pdfWindow: Window | null = null;
    pdfWindow = window.open('', '_blank');
    if (pdfWindow) {
      pdfWindow.document.write('<html><body><div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;"><div>Generating PDF... Please wait.</div></div></body></html>');
    }

    // Give Angular a tick to render the pdf-only-header and hide the toolbar
    setTimeout(async () => {
      const brand = this.brandFileName;
      const period = this.selectedPeriod.replace('/', '-');
      const filename = `SMRS_Fee_Report_${brand}_${period}.pdf`;
      
      const gridScrollable = element.querySelector('.data-grid-scrollable') as HTMLElement;
      const originalOverflow = gridScrollable ? gridScrollable.style.overflowX : '';
      if (gridScrollable) {
        gridScrollable.style.overflowX = 'visible'; // Prevent cutoff in PDF
      }

      // html2pdf lays the statement out inside a container as wide as the printable page (~960px) and captures only that box, so the
      // wide store table (one column per rate) was cut off. Lay the statement out at a fixed compact width (headers wrap), widen it only
      // if a table still overflows, and have html2canvas capture that full width; html2pdf then scales the capture to the page width.
      const originalWidth = element.style.getPropertyValue('width');
      const originalWidthPriority = element.style.getPropertyPriority('width');
      element.style.setProperty('width', `${PDF_BASE_WIDTH_PX}px`, 'important');
      const widestTable = Math.max(0, ...Array.from(element.querySelectorAll('table')).map(t => (t as HTMLElement).getBoundingClientRect().width));
      const horizontalChrome = gridScrollable ? Math.max(0, element.offsetWidth - gridScrollable.clientWidth) : 0;
      const requiredWidth = Math.ceil(Math.max(PDF_BASE_WIDTH_PX, element.scrollWidth, widestTable + horizontalChrome));
      element.style.setProperty('width', `${requiredWidth}px`, 'important');

      try {
        await this.pdfService.openPdf(element, filename, 'landscape', pdfWindow || undefined, requiredWidth);
      } catch (error) {
        console.error('Error opening PDF:', error);
        if (pdfWindow) pdfWindow.close();
        alert('Failed to open PDF.');
      } finally {
        if (gridScrollable) {
          gridScrollable.style.overflowX = originalOverflow;
        }
        if (originalWidth) element.style.setProperty('width', originalWidth, originalWidthPriority);
        else element.style.removeProperty('width');
        this.isExporting = false;
      }
    }, 100);
  }
}
